import React, { useState } from 'react';
import { useLazyQuery, useEvent } from "@soap/modules";
import { Button } from "../../ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../ui/dropdown-menu";
import { MoreHorizontal, Plus } from "lucide-react";
import { tagTypeKeys } from "../../../z50-domaincomp/constants/tag-type-keys";
import { AddTagDialog } from "../dialogs/AddTagDialog";
import { EditTagDialog } from "../dialogs/EditTagDialog";
import { RemoveTagDialog } from "../dialogs/RemoveTagDialog";

interface TagsManagementCardProps {
  tagType?: string;
}

export function TagsManagementCard({ tagType = 'student' }: TagsManagementCardProps) {
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [selectedTagId, setSelectedTagId] = useState<string>('');
  const [selectedTagName, setSelectedTagName] = useState<string>('');

  const [refreshTags, e103] = useLazyQuery("C119v1_GetTags", {
    C119_TagTypes: {
      SelectedKeys: [tagType]
    }
  });

  useEvent({
    eventName: "E105v1_TagAdded",
    onEventReceived: () => {
      refreshTags();
    }
  }, [refreshTags]);

  useEvent({
    eventName: "E108v1_TagUpdated",
    onEventReceived: () => {
      refreshTags();
    }
  }, [refreshTags]);

  useEvent({
    eventName: "E106v1_TagRemoved",
    onEventReceived: () => {
      refreshTags();
    }
  }, [refreshTags]);

  const handleEditClick = (tagId: string) => {
    setSelectedTagId(tagId);
    setEditDialogOpen(true);
  };

  const handleRemoveClick = (tagId: string, tagName: string) => {
    setSelectedTagId(tagId);
    setSelectedTagName(tagName);
    setRemoveDialogOpen(true);
  };

  const tagTypeKey = tagType === 'student' ? tagTypeKeys.Student : tagTypeKeys.Assessment;
  const tags = e103?.e103_Tags || [];

  return (
    <div className="space-y-4">
      {/* Add Tag Button */}
      <div className="flex justify-end">
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => setAddDialogOpen(true)}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Tag
        </Button>
      </div>
      
      {/* Tags Table */}
      {tags.length > 0 ? (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tag Name</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tags.map((tag: any) => (
                <TableRow key={tag.e103_LongId}>
                  <TableCell className="font-medium">
                    {tag.e103_Title}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEditClick(tag.e103_LongId)}>
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleRemoveClick(tag.e103_LongId, tag.e103_Title)}
                          className="text-destructive-action"
                        >
                          Remove
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          No tags found. Click "Add Tag" to create your first tag.
        </div>
      )}

      {/* Dialogs */}
      <AddTagDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        tagTypeKey={tagTypeKey}
        onTagAdded={() => refreshTags()}
      />

      <EditTagDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        tagId={selectedTagId}
        onTagUpdated={() => refreshTags()}
      />

      <RemoveTagDialog
        open={removeDialogOpen}
        onOpenChange={setRemoveDialogOpen}
        tagId={selectedTagId}
        tagName={selectedTagName}
        onTagRemoved={() => refreshTags()}
      />
    </div>
  );
}
